import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { ErrorMessage } from 'components';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Account } from 'services';
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  label: {
    fontSize: 18
  },
  datepicker: {
    marginTop: 0
  }
}));

const schema = {
  name_en: {
    presence: { allowEmpty: false, message: "is required" }
  },
  gender: {
    presence: { allowEmpty: false, message: "is required" }
  },
  birthdate: {
    presence: { allowEmpty: false, message: "is required" }
  },
};

const messages = {

}

const fields = [
  { field: "name_en", label: "Name", required: true, width: 6 },
  { field: "name_zh", label: "Name (ZH)", required: true, width: 6 },
  { field: "gender", label: "Gender", required: true, dropdown: true, options: [{ label: "Male", value: "male" }, { label: "Female", value: "female" }, { label: "Others", value: "Other" }], width: 6 },
]

const INIT_STATE = {
  values: {
    gender: "male",
    name_en: "",
    name_zh: "",
    birthdate: moment()
  },
  touched: {},
  errors: {}
}
let mounted = false;

const StudentAdd = props => {
  const { open, onClose, className, model, reload, ...rest } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const [formState, setFormState] = useState({ ...INIT_STATE });
  const { values } = formState;

  useEffect(() => {
    checkError(); // eslint-disable-next-line
  }, [formState.values]);

  useEffect(() => {
    mounted = true;
    return () => { mounted = false };
  }, [])

  const checkError = () => {
    const errors = validate(formState.values, schema);
    mounted && setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
    return errors;
  }

  useEffect(() => {
    if (mounted) {
      setFormState({ ...INIT_STATE });
      setErrorMessage("");
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleClose = event => {
    event.preventDefault();
    const errors = checkError();
    mounted && setFormState(formState => ({
      ...formState,
      touched: {
        // to show error message
        name_en: true,
      }
    }))
    if (errors && Object.keys(errors).length) return;

    setLoading(true);
    runAsyncBlock(async () => {
      await Account.add_student(model.id, { ...formState.values });
      reload();
    }).finally(() => {
      mounted && setLoading(false);
      onClose();
    });

  }

  const handleChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };

  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  const renderFields = () => {
    return fields.map(the_field => {
      let { field, label, required, options, dropdown, width } = the_field;
      return (
        <Grid key={field} item md={width} xs={width}>
          <TextField
            fullWidth
            label={label}
            name={field}
            helperText={
              hasError(field) ? formState.errors[field][0] : null
            }
            onChange={handleChange}
            // eslint-disable-next-line react/jsx-sort-props
            SelectProps={{ native: true }}
            value={values[field] || ""}
            variant="outlined"
            InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
            required={required}
            error={hasError(field)}
            select={dropdown}
          >
            {options && options.length > 0 && options.map((opt, index) => (
              <option key={index} value={opt.value}>{opt.label}</option>
            ))
            }
          </TextField>
        </Grid>
      );
    })
  }

  const handleDateChange = (date, field) => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [field]: date
      },
      touched: {
        ...formState.touched,
        [field]: true
      }
    }));
  };

  return (
    <Modal
      onClose={onClose}
      open={open}>
      <Card
        {...rest}
        className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Add Student
            </Typography>
            <Grid className={classes.container} container spacing={3}>
              {renderFields()}
              <Grid item md={6} xs={6}>
                <KeyboardDatePicker
                  className={classes.datepicker}
                  fullWidth
                  disableFuture
                  variant="inline"
                  format="YYYY-MM-DD"
                  margin="normal"
                  id="birthdate"
                  label="Date of Birth"
                  value={formState.values.birthdate}
                  onChange={(date) =>
                    handleDateChange(date, "birthdate")
                  }
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                />
              </Grid>
              <ErrorMessage message={errorMessage} map={messages} className={classes.error} />
              <Grid item />
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleClose} variant="contained" disabled={loading} >Add</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
};

StudentAdd.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool
};

StudentAdd.defaultProps = {
  open: false,
  onClose: () => { }
};

export default StudentAdd;
